import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";
import { getNextPaginatePage } from "@/utils";

import { type Review } from "../types";
import { reviewFeedback } from "./review-feedback";

export type ReviewList = Paginate<Review<true, true, true>>;

export const reviews = createQueryKeys("performance-reviews", {
  one: (id?: number) => [id],
  period: (periodId?: number) => ({
    queryKey: [periodId],
    contextQueries: {
      all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
      allInfinite: (filter?: SearchRequest) => [{ filter }],
    },
  }),
});

const include =
  "user.position,reviewPeriodTypes,peerReviewers.position,peerReviewers.objectives,managerPeerReviewers.position,managerPeerReviewers.objectives";

export const fetchReviewQueryOptions = (id?: number) =>
  queryOptions({
    ...reviews.one(id),
    queryFn: () =>
      api
        .get<Review<true, true, true>>(`/performance/reviews/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReview = (id?: number) =>
  useQuery(fetchReviewQueryOptions(id));

const periodReviewsInclude = "user.position";

export const fetchPeriodReviewsQueryOptions = (
  periodId: number,
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...reviews.period(periodId)._ctx.all(page, search),
    queryFn: () =>
      api
        .get<ReviewList>(`/performance/review-periods/${periodId}/reviews`, {
          params: { page, include: periodReviewsInclude, ...search },
        })
        .then((response) => response.data),
    enabled: periodId !== undefined,
  });

export const useFetchPeriodReviews = (
  periodId: number,
  search?: SearchRequest,
) =>
  usePaginateQuery((page) =>
    fetchPeriodReviewsQueryOptions(periodId, page, search),
  );

export const fetchInfinitePeriodReviewsQueryOptions = (
  periodId: number,
  search?: SearchRequest,
) =>
  infiniteQueryOptions({
    ...reviews.period(periodId)._ctx.allInfinite(search),
    queryFn: ({ pageParam }) =>
      api
        .get<ReviewList>(`/performance/review-periods/${periodId}/reviews`, {
          params: {
            page: pageParam,
            include: periodReviewsInclude,
            ...search,
          },
        })
        .then((response) => response.data),
    enabled: periodId !== undefined,
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchInfinitePeriodReviews = (
  periodId: number,
  search?: SearchRequest,
) =>
  useInfiniteQuery({
    ...fetchInfinitePeriodReviewsQueryOptions(periodId, search),
  });

export interface ReviewAddPeerRequest {
  users: {
    id: number;
    objective_ids: number[];
  }[];
}

export const useAddPeerFeedback = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReviewAddPeerRequest) =>
      api
        .post(`/performance/reviews/${id}/peer`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries(reviews.one(id));
      queryClient.invalidateQueries(reviewFeedback.review(id));
    },
  });
};

export interface ReviewAddManagerPeerRequest {
  users: {
    id: number;
    objective_ids: number[];
  }[];
}

export const useAddManagerPeerFeedback = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReviewAddManagerPeerRequest) =>
      api
        .post(`/performance/reviews/${id}/manager-peer`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries(reviews.one(id));
      queryClient.invalidateQueries(reviewFeedback.review(id));
    },
  });
};

export interface ReviewCalibrateRequest {
  rating: string | null;
  comment: string | null;
  elements: {
    id: number;
    value: any;
  }[];
}

export const useCalibrateReview = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ReviewCalibrateRequest) =>
      api
        .post(`/performance/reviews/${id}/calibrate`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries(reviews.one(id));
      queryClient.invalidateQueries(reviewFeedback.review(id));
    },
  });
};
